import { setPassword, setUkek, setUserId } from '@/store/slices/auth/authSlice';
import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';
import { isAPIError } from '@/store/utils';
import { logger } from '@/utils/logger';
import type { SetPasswordArgs } from '@/types/shared/store.types';
import { getUserIdFromToken } from '@/store/utils/jwtDecoder';

import { withLoader } from '../thunkWrappers';

const log = logger('six3ro:auth:setPassword');

/**
 * Object-based thunk using createAppAsyncThunk
 * Pattern: createAppAsyncThunk<Result, Args>
 */
// eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- void
export const setPasswordThunk = createAppAsyncThunk<void, SetPasswordArgs>(
  'auth/setPassword',
  withLoader(
    async({ password }, { getState, dispatch, extra: { authService, pocServices: { cryptoService } }, rejectWithValue }) => {
      try {
        log.info('🚀 Starting setPassword thunk');

        const state = getState();
        const username = state.auth.username;
        const token: string | undefined = state.auth.registrationToken ?? state.auth.recoveryToken;
        log.debug('Retrieved username from state', { username, hasToken: token !== undefined });
        if (token === undefined) {
          log.warn('Missing registration/recovery token in state');
          return rejectWithValue({ message: 'Missing registration or recovery token' });
        }

        log.info('📡 Step 1: Calling authService.setPassword');
        await authService.setPassword({ username, password });
        const userId = getUserIdFromToken(token);
        log.info('✅ Step 1 complete: Password set successfully', { userId });

        log.info('🔐 Step 2: Deriving UKEK from password');
        const ukek = await cryptoService.deriveKey(password, username);
        log.info('✅ Step 2 complete: UKEK derived', { ukekLength: ukek.length });

        log.info('✅ Step 3 complete: Session token retrieved');

        log.info('💾 Step 4: Dispatching state updates');
        dispatch(setUkek(ukek));
        dispatch(setPassword(password));
        dispatch(setUserId(userId));
        log.info('✅ Step 4 complete: State updated');

        log.info('🎉 setPassword thunk completed successfully');

      } catch (error) {
        log.error('❌ Error in setPassword thunk', error);

        if (isAPIError(error) && error.fieldErrors != null) {
          log.warn('API error with field errors', { fieldErrors: error.fieldErrors });
          // Correct: rejectWithValue with expected shape
          return rejectWithValue({ fieldErrors: error.fieldErrors });
        }

        log.error('Rethrowing non-API error');
        throw error;
      }
    }
  )
);
